import React, { FunctionComponent, MouseEvent, KeyboardEvent } from 'react';
import { Box, Text, Heading } from '@wix/design-system';
import { useRouter } from 'next/router';
import { ArticleType, Category } from '@wix/answers-api';
import Image from 'next/image';
import DeprecatedSearchResultCategories from './DeprecatedSearchResultCategories';
import { DATA_HOOKS } from '../../dataHooks';
import { BI, TOOLTIP_ENTER_DELAY } from '../../constants';
import { useBI } from '../../hooks/useBI';
import { clickOnAnswer } from '@wix/bi-logger-new-help-center/v2';
import { mapArticleTypeToItemType } from '../../utils/bi';
import { ArticleSearchResultItem } from '../../types';
import { InnerLink } from '../InnerLink';
import css from './index.module.scss';
import { RenderHTML } from '../RenderHTML';

type SearchResultsProps = {
  term: string;
  searchResults: ArticleSearchResultItem[];
  categoriesPaths: Record<string, Category[]>;
};

/**
 * @deprecated use `components/SearchResults/index.tsx` instead
 */
export const DeprecatedSearchResults: FunctionComponent<SearchResultsProps> = ({
  term,
  searchResults,
  categoriesPaths,
}: SearchResultsProps) => {
  const { sendBIEvent } = useBI();
  const { locale } = useRouter();

  const onSearchResultClick = async (
    e: MouseEvent | KeyboardEvent,
    url: string,
    searchResultArticleId: string,
    type: ArticleType,
    index: string
  ) => {
    await sendBIEvent(
      clickOnAnswer({
        source_name: BI.SOURCE_NAMES.SEARCH_RESULTS,
        kb_lang: locale as string,
        clicked_url: url,
        clicked_item_id: searchResultArticleId,
        clicked_item_order: index,
        question: term,
        clicked_item_type: mapArticleTypeToItemType(type),
        search_result_selection:
          BI.SEARCH_RESULT_SELECTION.FULL_SEARCH_RESULTS_LIST,
      })
    );
  };

  return (
    <Box direction="vertical" className={css.searchResults}>
      {searchResults.map((searchResult, index) => (
        <Box verticalAlign="middle" className={css.searchResult} key={index}>
          <Image
            src={`https://static.parastorage.com/services/kb-client-nextjs/dist/public/images/icons/document.svg`}
            alt=""
            height="31"
            width="24"
            className={css.searchResultIcon}
          />
          <Box direction="vertical" className={css.searchResultText} gap="7px">
            <InnerLink
              href={`${searchResult.uri}`}
              dataHook={DATA_HOOKS.SEARCH_RESULT_CATEGORIES_LINK}
              onClick={(e: MouseEvent | KeyboardEvent) =>
                onSearchResultClick(
                  e,
                  searchResult.url,
                  searchResult.id,
                  searchResult.type,
                  `${index + 1}`
                )
              }
            >
              <Box
                direction="vertical"
                gap="7px"
                width="100%"
                className={css.titleAndDescriptionWrapper}
              >
                <Heading
                  size="medium"
                  disabled
                  ellipsis
                  className={css.searchResultTitle}
                  dataHook={DATA_HOOKS.SEARCH_RESULT_CATEGORIES_TITLE}
                >
                  {searchResult.title}
                </Heading>
                <Text
                  size="medium"
                  maxLines={2}
                  ellipsis
                  tooltipProps={{enterDelay: TOOLTIP_ENTER_DELAY}}
                  disabled
                  className={css.searchResultDescription}
                  dataHook={DATA_HOOKS.SEARCH_RESULT_CATEGORIES_DESCRIPTION}
                >
                  {<RenderHTML html={searchResult.description} />}
                </Text>
              </Box>
            </InnerLink>
            <DeprecatedSearchResultCategories
              categoriesPaths={categoriesPaths}
              searchResult={searchResult}
              searchResultIndex={`${index + 1}`}
            />
          </Box>
        </Box>
      ))}
    </Box>
  );
};
